import React, { useMemo, useCallback } from 'react';
import { func, shape, string } from 'prop-types';
import { FormattedMessage } from 'react-intl';

import routeConfiguration from '../../routeConfiguration';
import { ensureCurrentUser } from '../../util/data';
import { pathByRouteName } from '../../util/routes';
import { PrimaryButton } from '../../components';
import { ModalSwimmy, ModalOverlay, ModalContent, ModalHeader, ModalBody } from '../ModalSwimmy';
import css from './ProvidersKycModal.css';

const PROVIDERS_KYC_MODAL_WHITELIST = [
  'LoginPage',
  'SignupPage',
  'AccountPersonalInfosPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'AccountPayoutDetailsPage',
];

const ProvidersKycModal = (props) => {
  const {
    currentUser,
    location,
    onManageDisableScrolling,
    currentUserHasPublishedListings: isProvider,
  } = props;

  const routes = useMemo(() => routeConfiguration(), []);
  const personalInfosPath = useMemo(
    () => pathByRouteName('AccountPersonalInfosPage', routes),
    [routes]
  );

  const whitelistedPaths = useMemo(
    () => PROVIDERS_KYC_MODAL_WHITELIST.map((page) => pathByRouteName(page, routes)),
    [routes]
  );

  const hasMissingKycInfos = useCallback(
    (user) => {
      if (!isProvider) return false;

      const protectedData = user?.attributes?.profile?.protectedData;
      const privateData = user?.attributes?.profile?.privateData;
      const birthdayDate = protectedData?.birthdayDate;
      const birthPlace = protectedData?.birthPlace;
      const taxIdNumber = privateData?.taxIdNumber;
      const hasNoTaxIdNumber = !!privateData?.hasNoTaxIdNumber;
      const hasTaxIdNumber =
        typeof taxIdNumber === 'string' ? taxIdNumber.trim().length > 0 : !!taxIdNumber;
      const isTaxIdConditionMissing = !hasTaxIdNumber && !hasNoTaxIdNumber;

      return !birthdayDate || !birthPlace || isTaxIdConditionMissing;
    },
    [isProvider]
  );

  const user = useMemo(() => ensureCurrentUser(currentUser), [currentUser]);

  const missingKyc = useMemo(
    () => Boolean(user?.id) && hasMissingKycInfos(user),
    [user, hasMissingKycInfos, isProvider]
  );

  const isPageWhitelisted = useMemo(
    () => whitelistedPaths.includes(location.pathname),
    [whitelistedPaths, location.pathname]
  );
  const isOpen = missingKyc && !isPageWhitelisted;

  return (
    <ModalSwimmy
      id="ProvidersKycModal"
      isOpen={isOpen}
      handleClickOutside={false}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <ModalOverlay />
      <ModalContent className={css.modalContent}>
        <ModalHeader heading={<FormattedMessage id="ProvidersKycModal.title" />} />
        <ModalBody className={css.modalBody}>
          <p className={css.modalText}>
            <FormattedMessage id="ProvidersKycModal.body" />
          </p>
          <PrimaryButton
            className={css.ctaButton}
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location = personalInfosPath;
              }
            }}
          >
            <FormattedMessage id="ProvidersKycModal.cta" />
          </PrimaryButton>
        </ModalBody>
      </ModalContent>
    </ModalSwimmy>
  );
};

ProvidersKycModal.defaultProps = {
  className: null,
  rootClassName: null,
  currentUser: null,
};

ProvidersKycModal.propTypes = {
  className: string,
  rootClassName: string,
  currentUser: shape({}),
  location: shape({
    pathname: string,
  }).isRequired,
  onManageDisableScrolling: func.isRequired,
};

export default ProvidersKycModal;
